/**
 * A cashflow & Age Pension plan in everyday words (cashflow rebuild, 21 Oct 2026; the agreed mock-up's plans
 * screen, and the village's rvPlanWords.ts).
 *
 * The engine's names ("Lifetime annuity (nil residual)", "Funeral bonds — one each") were written for an
 * adviser's list, and the review found them on the client's screen. The card says what the plan does with the
 * money, how much, and from which accounts — read from the candidate AS RUN, never from its wording. The same
 * reading gives the plan's parts (for the risks and the advice document), the money it puts out of reach (for
 * the plan choice rule), and each part's plain explanation.
 *
 * Presentation only: nothing here is computed beyond sums of the candidate's own fields.
 */
import { formatCurrency } from '../../utils/index.ts';
import type { LedgerChange } from '../../components/strategy/CauseEffectLedger.tsx';
import { householdVoice } from '../../utils/householdWords.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { RiskKind } from '../../strategy/strategyRisks.ts';
import { rvDrawWords } from '../retirementVillage/rvPlanWords.ts';
import { piecesForKinds } from '../advice/moduleSoa.ts';
import type { PlanPiece } from '../../components/narrative/PlanPieces.tsx';
import { readMeansTestPosition, type MeansTestBreakdownRow } from '../../components/insights/MeansTestPosition.tsx';
import { ANNUAL_REDUCTION_PER_1000_ASSETS } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { FUNERAL_BOND_LIMIT, GIFTING_LIMIT_ANNUAL } from '../../constants/index.ts';
import { GIFTING_LIMIT_5_YEAR } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { annuityHorizonFlag, type HorizonFactFind } from '../agedCare/strategyHorizonFlags.ts';

/** The fields a candidate (tested or hand-built) carries; read defensively. */
export interface CpCandidateLike {
    id?: string;
    name?: string;
    changeNothing?: boolean;
    funeralBondPurchase?: number;
    giftingAmount?: number;
    annuity?: Record<string, unknown> | null;
    superContribution?: { client?: number; partner?: number; commenceIncomeStream?: boolean } | null;
    investmentBond?: number;
    debtPayoff?: { payments?: Record<string, number> } | Record<string, unknown> | null;
    fundingSources?: Array<{ type: string; id: string; amountToTake: number }>;
}

const num = (v: unknown) => Math.max(0, Number(v) || 0);
const money = (n: number) => formatCurrency(Math.round(n));
const joinAnd = (xs: string[]) => (xs.length <= 1 ? xs[0] || '' : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** What the plan puts where, in dollars. */
export interface CpAmounts {
    funeralBond: number;
    gift: number;
    annuity: number;
    annuityType: 'lifetime' | 'guaranteed';
    /** 0 (income only) to 1 (money back). */
    residual: number;
    superTotal: number;
    superClient: number;
    superPartner: number;
    investmentBond: number;
    debt: number;
}

export function cpAmountsOf(c: CpCandidateLike | null | undefined): CpAmounts {
    const a = (c?.annuity || {}) as { purchaseAmount?: unknown; annuityType?: unknown; residualCapital?: unknown };
    const payments = ((c?.debtPayoff as { payments?: Record<string, number> } | null | undefined)?.payments) || {};
    const superClient = num(c?.superContribution?.client);
    const superPartner = num(c?.superContribution?.partner);
    return {
        funeralBond: num(c?.funeralBondPurchase),
        gift: num(c?.giftingAmount),
        annuity: num(a.purchaseAmount),
        annuityType: a.annuityType === 'guaranteed' ? 'guaranteed' : 'lifetime',
        residual: Math.max(0, Math.min(1, Number(a.residualCapital) || 0)),
        superClient,
        superPartner,
        superTotal: superClient + superPartner,
        investmentBond: num(c?.investmentBond),
        debt: Object.values(payments).reduce((t, v) => t + num(v), 0),
    };
}

/**
 * The plan's parts, read from what it does — so a hand-built plan ('cp-manual') has parts too. Reading them
 * from the candidate's id alone left a hand-built plan with no risks and no plan pieces.
 */
export function cpKindsOf(c: CpCandidateLike | null | undefined): RiskKind[] {
    if (!c || c.changeNothing) return ['stayPut'];
    const m = cpAmountsOf(c);
    const kinds: RiskKind[] = [];
    if (m.superTotal > 0) kinds.push('superContribution');
    if (m.investmentBond > 0) kinds.push('investmentBond');
    if (m.annuity > 0) kinds.push(m.annuityType === 'guaranteed' ? 'annuityGuaranteed' : m.residual > 0 ? 'annuityReturned' : 'annuityNil');
    if (m.funeralBond > 0) kinds.push('funeralBond');
    if (m.gift > 0) kinds.push('gift');
    if (m.debt > 0) kinds.push('clearDebt');
    return kinds;
}

/**
 * The money a plan puts OUT OF REACH, from the client's side (the plan choice rule, owner 20 Oct 2026): a
 * gift is gone, a funeral bond is kept for the funeral, a guaranteed income is committed for life. Money in
 * super or an investment bond is still theirs to draw on, and debt repaid is not money tied up.
 */
export function cpTiedUpOf(c: CpCandidateLike | null | undefined): number {
    const m = cpAmountsOf(c);
    return m.funeralBond + m.gift + m.annuity;
}

/** "the guaranteed income", "the funeral bonds and the gift" — what the plan itself spent accounts on. */
export function cpSpentOnWords(c: CpCandidateLike | null | undefined): string {
    const m = cpAmountsOf(c);
    const parts = [
        m.annuity > 0 ? 'the guaranteed income' : null,
        m.funeralBond > 0 ? 'the funeral bond' : null,
        m.gift > 0 ? 'the gift' : null,
        m.superTotal > 0 ? 'super' : null,
        m.investmentBond > 0 ? 'the investment bond' : null,
        m.debt > 0 ? 'repaying the debt' : null,
    ].filter(Boolean) as string[];
    return parts.length ? joinAnd(parts) : 'the plan';
}

/** The guaranteed income's type, said by what the family receives (guaranteed income review, 21 Oct 2026). */
export function cpAnnuityWords(m: CpAmounts): { short: string; long: string } {
    if (m.annuityType === 'guaranteed') return { short: 'with the full amount back to the family', long: 'bought with life cover, so the full amount goes to the family whenever death comes' };
    if (m.residual > 0) return { short: 'with money back in the early years', long: 'that pays the money back to the family if death comes in the early years, falling to nothing later' };
    return { short: 'that stops at death', long: 'that pays the most income for life, with nothing back to the family' };
}

/** The card's title and the line under it. */
export function cpPlanWords(c: CpCandidateLike, factFind: DraftFactFind): { title: string; sub: string } {
    const v = householdVoice(factFind);
    const m = cpAmountsOf(c);
    const id = String(c.id || '');
    const couple = factFind?.adviceFor === 'couple';
    const clientName = factFind?.client?.firstName || (v.you ? 'you' : 'the client');
    const partnerName = factFind?.partner?.firstName || (v.you ? 'your partner' : 'the partner');
    if (c.changeNothing) return { title: 'Change nothing', sub: `Everything stays as it is today, projected over the same years. Every plan is measured against this.` };

    const funding: Record<string, number> = {};
    for (const s of c.fundingSources || []) funding[`${s.type}-${s.id}`] = (funding[`${s.type}-${s.id}`] || 0) + num(s.amountToTake);
    const drawn = rvDrawWords(funding, factFind, 'the money');
    const from = drawn ? ` ${cap(drawn)}.` : '';
    const pensionMax = /-max(-|$)/.test(id);

    // One lever, said as the card's whole title.
    const parts: Array<{ title: string; sub: string }> = [];
    if (m.annuity > 0) {
        const w = cpAnnuityWords(m);
        parts.push({
            title: `A guaranteed income for life ${w.short}`,
            sub: `Put ${money(m.annuity)} into a guaranteed income for life (a lifetime annuity) ${w.long}.${pensionMax ? ' This is the amount that lifts the Age Pension most.' : ''}`,
        });
    }
    if (m.funeralBond > 0) {
        const each = couple && m.funeralBond >= FUNERAL_BOND_LIMIT * 2 - 1;
        parts.push({
            title: each ? 'Funeral bonds, one each' : 'A funeral bond',
            sub: `Put ${money(m.funeralBond)} into ${each ? `funeral bonds, ${money(m.funeralBond / 2)} each` : 'a funeral bond'}, set aside for the funeral. Centrelink stops counting it.`,
        });
    }
    if (m.gift > 0) {
        parts.push({
            title: `Give ${money(m.gift)} to family`,
            sub: m.gift <= GIFTING_LIMIT_ANNUAL
                ? `Give ${money(m.gift)} to family now, within the yearly free area, so Centrelink stops counting it straight away.`
                : `Give ${money(m.gift)} to family now. Only ${money(GIFTING_LIMIT_ANNUAL)} a year is free; the rest still counts for five years.`,
        });
    }
    if (m.superTotal > 0) {
        const split = couple && m.superClient > 0 && m.superPartner > 0
            ? `${money(m.superClient)} for ${clientName} and ${money(m.superPartner)} for ${partnerName}`
            : m.superPartner > 0 ? `for ${partnerName}` : couple ? `for ${clientName}` : '';
        parts.push({
            title: `Move ${money(m.superTotal)} of savings into super`,
            sub: `Contribute ${money(m.superTotal)} to super after tax${split ? `, ${split}` : ''}, where its earnings are taxed at 15% or nothing once it pays a pension, instead of at ${v.their} own rate. It stays ${v.you ? 'yours' : 'theirs'} to draw on.`,
        });
    }
    if (m.investmentBond > 0) {
        parts.push({
            title: `Move ${money(m.investmentBond)} of savings into an investment bond`,
            sub: `An investment bond pays its own tax inside (30%) and nothing on ${v.their} tax return; after ten years what comes out is tax-free. Centrelink counts it like the cash it replaces.`,
        });
    }
    if (m.debt > 0) {
        parts.push({ title: `Pay ${money(m.debt)} off ${v.their} debts`, sub: `Repay ${money(m.debt)} of what ${v.they} owe, dearest first, so the repayments stop.` });
    }

    if (parts.length === 0) return { title: 'Change nothing', sub: 'Nothing is moved.' };
    if (parts.length === 1) return { title: parts[0]!.title, sub: `${parts[0]!.sub}${from}`.trim() };
    // Two or more: the short names joined; the sub lists each one.
    const shortNames = parts.map((p, i) => (i === 0 ? p.title : p.title.charAt(0).toLowerCase() + p.title.slice(1)));
    return { title: joinAnd(shortNames), sub: `${parts.map((p) => p.sub).join(' ')}${from}`.trim() };
}

/** The plan's pieces in plain words, with their amounts — the same rows the advice document prints. */
export function cpPiecesOf(c: CpCandidateLike | null | undefined): PlanPiece[] {
    const m = cpAmountsOf(c);
    const kinds = cpKindsOf(c);
    const amounts: Partial<Record<RiskKind, number>> = {
        annuityNil: m.annuity, annuityReturned: m.annuity, annuityGuaranteed: m.annuity,
        funeralBond: m.funeralBond, gift: m.gift, superContribution: m.superTotal, investmentBond: m.investmentBond, clearDebt: m.debt,
    };
    return piecesForKinds(kinds, amounts).map((p, i) => ({ key: `${kinds[i] ?? i}`, name: p.name, what: p.what, amount: p.amount ?? null }));
}

/**
 * EVERY LEVER IN PLAIN WORDS (owner's brief, 21 Oct 2026: "every financial product or concept explained in
 * simple language"). What it is, what Centrelink does with it, and the catch — for the questions on step 3
 * and the ledger's set-ups.
 */
export interface LeverWords { name: string; what: string; centrelink: string; watch: string }
export const CP_LEVER_WORDS: Record<'bond' | 'gift' | 'annuity' | 'super' | 'investmentBond' | 'debt', LeverWords> = {
    bond: {
        name: 'A funeral bond',
        what: 'Money set aside now to pay for the funeral, held by a funeral fund or insurer until it is needed.',
        centrelink: `Up to ${money(FUNERAL_BOND_LIMIT)} each is not counted at all, so it can lift the Age Pension.`,
        watch: 'The money can’t be taken back for anything else; above the limit it is counted like savings.',
    },
    gift: {
        name: 'A gift to family, within the free area',
        what: 'Giving money to children or grandchildren now, rather than leaving it in the estate.',
        centrelink: `Up to ${money(GIFTING_LIMIT_ANNUAL)} a year (and ${money(GIFTING_LIMIT_5_YEAR)} over five years) stops counting straight away.`,
        watch: 'Anything above that still counts as yours, and is deemed to earn income, for five years, even though it has gone.',
    },
    annuity: {
        name: 'A guaranteed income for life',
        what: 'A lump sum handed to an insurer in return for a fixed income for as long as you live (a lifetime annuity).',
        centrelink: 'Only 60% of what goes in is counted at first (30% later), so it lowers what Centrelink counts more than anything else.',
        watch: 'The money is committed for life. Each type gives back a different amount to the family if death comes early; income only gives back nothing.',
    },
    super: {
        name: 'After-tax super',
        what: 'Savings put into super as an after-tax contribution, where the earnings are taxed at 15%, or not at all once it pays a pension.',
        centrelink: 'Counted and deemed like the cash it replaces, once you are of Age Pension age; so this is about tax, not the pension.',
        watch: 'Only allowed under 75, and within yearly limits set by your super balance.',
    },
    investmentBond: {
        name: 'An investment bond',
        what: 'An investment that pays its own tax inside (30%), with nothing on your tax return; after ten years what comes out is tax-free.',
        centrelink: 'Counted and deemed like the cash it replaces, so this is about tax, not the pension.',
        watch: 'Best left alone for ten years; taking money out sooner brings tax back.',
    },
    debt: {
        name: 'Paying off a debt',
        what: 'Using savings to repay what you owe, dearest first.',
        centrelink: 'Money owed against savings is already netted off, so repaying it rarely changes the pension.',
        watch: 'The repayments stop, which is money kept every year; the savings used are gone from the bank.',
    },
};

/**
 * "WHY THE AGE PENSION IS WHAT IT IS" (owner ruling, every module; the cp mock-up's step 1): which test sets
 * it, and how much moving money out of Centrelink's count would change it. Read from the engine's own
 * breakdown (readMeansTestPosition), never recomputed; null when the breakdown is not there.
 */
export interface CpPensionWhy {
    pension: number;
    assets: number;
    income: number;
    test: 'assets' | 'income';
    perThousand: number;
    toFull: number | null;
    atFull: boolean;
    fullOutOfReach: boolean;
    maxRate: number;
}

export function cpPensionWhyOf(row: { socialSecurity?: unknown; socialSecurityBreakdown?: unknown } | null | undefined, isCouple: boolean): CpPensionWhy | null {
    const pos = readMeansTestPosition(row?.socialSecurityBreakdown as MeansTestBreakdownRow[] | undefined, Number(row?.socialSecurity) || 0, isCouple);
    if (!pos) return null;
    return {
        pension: pos.pension,
        assets: pos.assessableAssets,
        income: pos.assessableIncome,
        test: pos.determinedBy === 'income' ? 'income' : 'assets',
        perThousand: ANNUAL_REDUCTION_PER_1000_ASSETS,
        toFull: pos.shiftForFullPension,
        atFull: pos.atFullPension,
        fullOutOfReach: pos.fullOutOfReach,
        maxRate: pos.maxRate,
    };
}

/** The why in one or two sentences, in the reader's voice. */
export function cpPensionWhyWords(w: CpPensionWhy, theirWord: string): string {
    if (w.atFull) return `Centrelink counts ${money(w.assets)} of ${theirWord} assets and ${money(w.income)} a year of income, and that is within the limits for the full pension, so there is nothing more to gain on the Age Pension.`;
    if (w.test === 'income') return `Centrelink counts ${money(w.assets)} of ${theirWord} assets and ${money(w.income)} a year of income, and at that level the income test sets the pension. Lowering what Centrelink counts lifts it only where it also lowers the income Centrelink counts (the income it assumes savings earn).`;
    if (w.pension < 1) return `Centrelink counts ${money(w.assets)} of ${theirWord} assets, more than the limit for any Age Pension, so the assets test sets it at nothing. Every $1,000 less that Centrelink counts, below that limit, would be worth about ${money(w.perThousand)} a year.`;
    const full = w.toFull != null && w.toFull > 0 && !w.fullOutOfReach ? `; about ${money(w.toFull)} less counted would bring the full pension` : '';
    return `Centrelink counts ${money(w.assets)} of ${theirWord} assets, and at that level the assets test sets the pension. Every $1,000 less that Centrelink counts adds about ${money(w.perThousand)} a year${full}.`;
}

/**
 * WHY THE PLAN DOES WHAT IT DOES, ON THE THREE LEVERS (owner's brief, 21 Oct 2026: "outputs show the impact on
 * the key levers … against today's position, with the why explained"). One plain sentence each for what is
 * left to the family, the money each year and the Age Pension, against changing nothing — the reasons read
 * from what the plan does, the figures the engine's own.
 */
export function cpWhyLines(
    plan: { estate: number; cashflow: number; pension: number },
    base: { estate: number; cashflow: number; pension: number } | null,
    c: CpCandidateLike | null | undefined,
    years: number,
    v: { you: boolean; their: string; they: string },
    annuityIncome: number | null = null,
): string[] {
    if (!base) return [];
    const m = cpAmountsOf(c);
    const out: string[] = [];
    const dE = plan.estate - base.estate;
    const dP = plan.pension - base.pension;
    const dC = plan.cashflow - base.cashflow;
    const outOfCount = [
        m.funeralBond > 0 ? `the ${money(m.funeralBond)} funeral bond is not counted` : null,
        m.gift > 0 ? `the ${money(m.gift)} gift ${m.gift <= GIFTING_LIMIT_ANNUAL ? 'stops being counted straight away' : 'stops being counted only up to the yearly limit'}` : null,
        m.annuity > 0 ? `only part of the ${money(m.annuity)} guaranteed income is counted (60% at first${m.annuityType === 'guaranteed' ? ', and the life cover in full' : ''})` : null,
    ].filter(Boolean) as string[];
    // The estate.
    const estateWhy = [
        m.gift > 0 ? 'the gift has already gone to the family' : null,
        m.annuity > 0 && m.annuityType !== 'guaranteed' && m.residual <= 0 ? 'nothing of the guaranteed income comes back' : null,
        m.annuity > 0 && m.annuityType !== 'guaranteed' && m.residual > 0 ? 'what comes back from the guaranteed income falls each year' : null,
        m.annuity > 0 && m.annuityType === 'guaranteed' ? 'the full amount of the guaranteed income comes back' : null,
        m.funeralBond > 0 ? 'the funeral bond pays for the funeral rather than being left to spend' : null,
        Math.abs(dP) >= 1 ? `${dP > 0 ? 'more' : 'less'} Age Pension adds up over the years` : null,
        Math.abs(dC) >= 1 ? `${dC > 0 ? 'less' : 'more'} is drawn from savings each year` : null,
        m.superTotal > 0 || m.investmentBond > 0 ? 'less tax is paid on what the money earns' : null,
    ].filter(Boolean) as string[];
    out.push(Math.abs(dE) < 1
        ? `What is left to the family after about ${years} years is the same as changing nothing.`
        : `It leaves the family ${money(Math.abs(dE))} ${dE > 0 ? 'more' : 'less'} than changing nothing after about ${years} years${estateWhy.length ? `, because ${joinAnd(estateWhy)}` : ''}.`);
    // The money each year.
    const cashWhy = [
        m.annuity > 0 ? `the guaranteed income pays ${annuityIncome && annuityIncome > 0 ? `about ${money(annuityIncome)} a year` : 'an income every year'}` : null,
        dP >= 1 ? 'the Age Pension is higher' : dP <= -1 ? 'the Age Pension is lower' : null,
        m.debt > 0 ? 'the debt repayments stop' : null,
        m.annuity + m.funeralBond + m.gift + m.superTotal + m.investmentBond > 0 ? 'the money moved no longer earns interest in the bank' : null,
    ].filter(Boolean) as string[];
    out.push(Math.abs(dC) < 1
        ? 'The money each year is the same as changing nothing.'
        : `Each year ${v.they} ${dC > 0 ? 'have' : 'are'} ${money(Math.abs(dC))} ${dC > 0 ? 'more to spend' : 'further short'} than changing nothing${cashWhy.length ? `: ${joinAnd(cashWhy)}` : ''}.`);
    // The Age Pension.
    out.push(Math.abs(dP) < 1
        ? `The Age Pension is the same as changing nothing${m.superTotal > 0 || m.investmentBond > 0 ? ': super and an investment bond are counted like the savings they replace, so this plan is about tax' : ''}.`
        : `The Age Pension is ${money(Math.abs(dP))} a year ${dP > 0 ? 'higher' : 'lower'}${outOfCount.length && dP > 0 ? `, because ${joinAnd(outOfCount)}, so Centrelink counts less of what ${v.they} own` : ''}.`);
    return out;
}

/**
 * THE AGE-AND-HEALTH WARNING FOR A PLAN'S GUARANTEED INCOME (owner, 22 Oct 2026: "What could go wrong" on a
 * chosen plan never said it) — the aged care rule (annuityHorizonFlag), as the village, downsizing and granny
 * flat apply it. Read from the plan as run (whose name it is in, its type, what comes back, whether it rises with
 * prices); a chosen card saved without its plan falls back to the card's own annuity, in the client's name.
 */
export function cpHorizonWarningOf(
    c: CpCandidateLike | null | undefined,
    factFind: DraftFactFind,
    cardAnnuity: { amount: number; type: string; residualPercent: number } | null = null,
): string | null {
    const a = (c?.annuity || null) as { purchaseAmount?: unknown; residualCapital?: unknown; ownership?: unknown; annuityType?: unknown; indexation?: unknown } | null;
    if (a && Number(a.purchaseAmount) > 0) {
        return annuityHorizonFlag({ factFind: factFind as unknown as HorizonFactFind, residualCapital: Number(a.residualCapital) || 0, owner: a.ownership, annuityType: a.annuityType, indexation: a.indexation })?.warning ?? null;
    }
    if (cardAnnuity && cardAnnuity.amount > 0) {
        return annuityHorizonFlag({ factFind: factFind as unknown as HorizonFactFind, residualCapital: (Number(cardAnnuity.residualPercent) || 0) / 100, owner: 'client', annuityType: cardAnnuity.type })?.warning ?? null;
    }
    return null;
}

/** One numbered line of Explain this: the cause in bold, then what it does to the numbers. */
export interface CpLedgerLine {
    key: 'pension' | 'annuity' | 'income' | 'tax' | 'estate';
    lead: string;
    body: string;
    /** The "from … to …" figure as data, for Explain this's colour (the body keeps the words). */
    change?: LedgerChange;
}

/** The first year's investment income and tax, where the plan's own projection rows are to hand. */
export interface CpFirstYear { investmentIncome: number; tax: number }

export const cpFirstYearOf = (rows: ReadonlyArray<{ investmentIncome?: unknown; tax?: unknown }> | null | undefined): CpFirstYear | null => {
    const r = rows?.[0];
    return r ? { investmentIncome: Number(r.investmentIncome) || 0, tax: Number(r.tax) || 0 } : null;
};

/**
 * EXPLAIN THIS AS AGED CARE TELLS IT (owner, 22 Oct 2026: "all modules use consistent UI"). The reasons the old
 * "Why, against changing nothing" bullets gave, said as cause → effect lines for the numbered ledger
 * (CauseEffectLedger): the Age Pension before → after and why, the guaranteed income's own payments, the
 * investment income and the tax the money moved changes (only from the plan's own projection rows — a saved card
 * keeps none, so those two lines are left out rather than guessed), and what the plan does to the family's share.
 * "Put together" (the money each year) and the "=" line (what is left) follow in the ledger itself.
 * Presentation only: every figure is the engine's own.
 */
/** Tax for the year in words: a negative figure is a refund. */
const taxWords = (n: number): string => (n < 0 ? `a refund of ${formatCurrency(Math.round(-n))}` : formatCurrency(Math.round(n)));

export function cpLedgerLines(i: {
    plan: { estate: number; cashflow: number; pension: number };
    base: { estate: number; cashflow: number; pension: number } | null;
    c: CpCandidateLike | null | undefined;
    v: { their: string; they: string };
    annuityIncome?: number | null;
    first?: CpFirstYear | null;
    baseFirst?: CpFirstYear | null;
}): CpLedgerLine[] {
    const { plan, base, v } = i;
    const m = cpAmountsOf(i.c);
    const out: CpLedgerLine[] = [];
    const their = cap(v.their);
    // The Age Pension, before → after, and what Centrelink stops counting.
    const outOfCount = [
        m.funeralBond > 0 ? `the ${money(m.funeralBond)} funeral bond is not counted` : null,
        m.gift > 0 ? `the ${money(m.gift)} gift ${m.gift <= GIFTING_LIMIT_ANNUAL ? 'stops being counted straight away' : 'stops being counted only up to the yearly limit'}` : null,
        m.annuity > 0 ? `only part of the ${money(m.annuity)} guaranteed income is counted (60% at first${m.annuityType === 'guaranteed' ? ', and the life cover in full' : ''})` : null,
    ].filter(Boolean) as string[];
    const taxOnly = m.superTotal > 0 || m.investmentBond > 0;
    if (!base) {
        out.push({ key: 'pension', lead: `${their} Age Pension is ${money(plan.pension)} a year`, body: outOfCount.length ? `: ${joinAnd(outOfCount)}.` : '.' });
    } else {
        const dP = plan.pension - base.pension;
        if (Math.abs(dP) < 1) {
            out.push({ key: 'pension', lead: `${their} Age Pension stays at ${money(plan.pension)} a year`, body: taxOnly ? ': super and an investment bond are counted like the savings they replace, so this plan is about tax.' : '.' });
        } else if (dP > 0) {
            const after = `${outOfCount.length ? `: ${joinAnd(outOfCount)}` : ''}.`;
            out.push({ key: 'pension', lead: `Centrelink counts less, so ${v.their} Age Pension rises`, body: ` from ${money(base.pension)} to ${money(plan.pension)} a year${after}`, change: { from: base.pension, to: plan.pension, unit: ' a year', better: 'up', after } });
        } else {
            out.push({ key: 'pension', lead: `Centrelink counts more, so ${v.their} Age Pension falls`, body: ` from ${money(base.pension)} to ${money(plan.pension)} a year.`, change: { from: base.pension, to: plan.pension, unit: ' a year', better: 'up', after: '.' } });
        }
    }
    // The guaranteed income's own payments.
    if (m.annuity > 0) {
        const inc = Number(i.annuityIncome) || 0;
        out.push({
            key: 'annuity',
            lead: inc > 0 ? `The guaranteed income pays about ${money(inc)} a year` : 'The guaranteed income pays an income every year',
            body: `, for life, on top of the Age Pension — ${m.annuityType === 'guaranteed' ? 'and the full amount goes back to the family at death' : m.residual > 0 ? 'with money back to the family if death comes in the early years' : 'with nothing back to the family at death'}.`,
        });
    }
    // Investment income and tax: only from the plan's own rows.
    if (i.first && i.baseFirst) {
        const di = i.first.investmentIncome - i.baseFirst.investmentIncome;
        if (Math.abs(di) >= 100) {
            out.push({
                key: 'income',
                lead: di < 0 ? 'Less money stays in the bank, so investment income falls' : 'More money is invested, so investment income rises',
                body: ` from ${money(i.baseFirst.investmentIncome)} to ${money(i.first.investmentIncome)} in the first year.${di < 0 ? ' That is part of the cost of the plan.' : ''}`,
                change: { from: i.baseFirst.investmentIncome, to: i.first.investmentIncome, unit: ' in the first year', better: 'up', after: `.${di < 0 ? ' That is part of the cost of the plan.' : ''}` },
            });
        }
        const dt = i.first.tax - i.baseFirst.tax;
        if (Math.abs(dt) >= 100) {
            out.push({
                key: 'tax',
                lead: dt < 0 ? 'Less tax is paid' : 'More tax is paid',
                // A negative figure is a refund, said as one (live check, 22 Oct 2026: "-$600 … against -$2,400").
                body: ` — ${taxWords(i.first.tax)} in the first year, against ${taxWords(i.baseFirst.tax)} changing nothing${dt < 0 && taxOnly ? `, because ${m.superTotal > 0 && m.investmentBond > 0 ? 'super and the investment bond pay' : m.superTotal > 0 ? 'super pays' : 'the investment bond pays'} lower tax on what the money earns than ${v.their} own rate` : ''}.`,
            });
        }
    }
    // What the plan itself does to the family's share (the "=" line gives the figure).
    const estateWhy = [
        m.gift > 0 ? 'the gift has already gone to the family' : null,
        m.annuity > 0 && m.annuityType !== 'guaranteed' && m.residual <= 0 ? 'nothing of the guaranteed income comes back' : null,
        m.annuity > 0 && m.annuityType !== 'guaranteed' && m.residual > 0 ? 'what comes back from the guaranteed income falls each year' : null,
        m.annuity > 0 && m.annuityType === 'guaranteed' ? 'the full amount of the guaranteed income comes back' : null,
        m.funeralBond > 0 ? 'the funeral bond pays for the funeral rather than being left to spend' : null,
    ].filter(Boolean) as string[];
    if (estateWhy.length) out.push({ key: 'estate', lead: 'For what is left to the family', body: `, ${joinAnd(estateWhy)}.` });
    return out;
}
